import { Command } from "commander";
import {
  detectInstalledSkillAgents,
  discoverSkills,
  getUniversalSkillAgents,
  installSkillsFromSource,
  isSkillAgentType,
  skillAgents,
  type Skill,
  type SkillAgentType,
} from "agent-install/skill";
import { DOCS_URL } from "../utils/constants.js";
import { handleError } from "../utils/handle-error.js";
import { highlighter } from "../utils/highlighter.js";
import { detectNonInteractive } from "../utils/is-non-interactive.js";
import { logger } from "../utils/logger.js";
import { printBanner } from "../utils/print-banner.js";
import { prompts } from "../utils/prompts.js";
import { resolveBundledSkillsDir } from "../utils/resolve-skills-dir.js";
import { spinner } from "../utils/spinner.js";

const VERSION = process.env.VERSION ?? "0.0.1";

interface InitOptions {
  yes?: boolean;
  skill?: string[];
  agent?: string[];
  global?: boolean;
  copy?: boolean;
  cwd: string;
}

interface ResolvedSelection {
  skills: Skill[];
  agents: SkillAgentType[];
}

const filterSkillsBySelection = (all: Skill[], selection: string[] | undefined): Skill[] => {
  if (!selection || selection.length === 0) return all;
  const wanted = new Set(selection);
  return all.filter((innerSkill) => wanted.has(innerSkill.name));
};

const coerceAgentSelection = (input: string[] | undefined): SkillAgentType[] | undefined => {
  if (!input || input.length === 0) return undefined;
  const mapped = input.filter(isSkillAgentType);
  return mapped.length > 0 ? mapped : undefined;
};

const resolveDefaultAgents = async (): Promise<SkillAgentType[]> => {
  const detected = await detectInstalledSkillAgents();
  if (detected.length > 0) return detected;
  return getUniversalSkillAgents();
};

const promptForSkills = async (availableSkills: Skill[]): Promise<Skill[]> => {
  if (availableSkills.length === 1) return availableSkills;

  const { selectedSkillNames } = await prompts({
    type: "multiselect",
    name: "selectedSkillNames",
    message: "Select skills to install",
    hint: "Space to toggle, Enter to confirm",
    instructions: false,
    min: 1,
    choices: availableSkills.map((innerSkill) => ({
      title: innerSkill.name,
      value: innerSkill.name,
      selected: true,
    })),
  });

  if (!Array.isArray(selectedSkillNames) || selectedSkillNames.length === 0) {
    logger.break();
    logger.log("No skills selected. Nothing to do.");
    logger.break();
    process.exit(1);
  }

  const wanted = new Set(selectedSkillNames);
  return availableSkills.filter((innerSkill) => wanted.has(innerSkill.name));
};

const promptForAgents = async (defaults: SkillAgentType[]): Promise<SkillAgentType[]> => {
  const defaultSet = new Set(defaults);
  const { selectedAgents } = await prompts({
    type: "multiselect",
    name: "selectedAgents",
    message: "Select agents to install React Stack skills into",
    hint: "Space to toggle, Enter to confirm",
    instructions: false,
    min: 1,
    choices: Object.values(skillAgents)
      .filter((config) => config.name !== "universal")
      .map((config) => ({
        title: config.displayName,
        value: config.name,
        selected: defaultSet.has(config.name),
      })),
  });

  if (!Array.isArray(selectedAgents) || selectedAgents.length === 0) {
    logger.break();
    logger.log("No agents selected. Nothing to do.");
    logger.break();
    process.exit(1);
  }

  return selectedAgents.filter(isSkillAgentType);
};

const resolveSelection = async (
  options: InitOptions,
  availableSkills: Skill[],
  isNonInteractive: boolean,
): Promise<ResolvedSelection> => {
  const skillFromFlag = filterSkillsBySelection(availableSkills, options.skill);

  if (isNonInteractive) {
    const agentsFromFlag = coerceAgentSelection(options.agent);
    const agents = agentsFromFlag ?? (await resolveDefaultAgents());
    return { skills: skillFromFlag, agents };
  }

  const hasSkillFlag = Boolean(options.skill && options.skill.length > 0);
  const resolvedSkills = hasSkillFlag ? skillFromFlag : await promptForSkills(availableSkills);

  const hasAgentFlag = Boolean(options.agent && options.agent.length > 0);
  const agentsFromFlag = coerceAgentSelection(options.agent);
  const defaultAgents = agentsFromFlag ?? (await resolveDefaultAgents());
  const resolvedAgents = hasAgentFlag ? defaultAgents : await promptForAgents(defaultAgents);

  return { skills: resolvedSkills, agents: resolvedAgents };
};

const installSelectedSkills = async (
  skillsDir: string,
  selection: ResolvedSelection,
  options: InitOptions,
): Promise<void> => {
  let installedCount = 0;
  let failedCount = 0;

  for (const innerSkill of selection.skills) {
    const installSpinner = spinner(`Installing ${highlighter.info(innerSkill.name)}.`).start();
    try {
      const result = await installSkillsFromSource({
        source: skillsDir,
        agents: selection.agents,
        skills: [innerSkill.name],
        global: options.global,
        mode: options.copy ? "copy" : "symlink",
        cwd: options.cwd,
      });
      installedCount += result.installed.length;
      failedCount += result.failed.length;
      if (result.failed.length > 0) {
        installSpinner.warn(
          `Installed ${highlighter.info(innerSkill.name)} to ${result.installed.length} agent(s); ${result.failed.length} failed.`,
        );
        for (const record of result.failed) {
          logger.error(`  ${record.agent}: ${record.error}`);
        }
      } else {
        installSpinner.succeed(
          `Installed ${highlighter.info(innerSkill.name)} to ${result.installed.length} agent(s).`,
        );
      }
    } catch (error) {
      installSpinner.fail(`Failed to install ${highlighter.info(innerSkill.name)}.`);
      failedCount += 1;
      if (error instanceof Error) {
        logger.error(`  ${error.message}`);
      }
    }
  }

  logger.break();
  if (installedCount > 0) {
    logger.success(
      `React Stack installed. Restart your agent(s) so they pick up the new skills.`,
    );
    logger.break();
    logger.log(`${highlighter.bold("Get started")} by asking your agent:`);
    logger.log(`  ${highlighter.info("/react-ui")} build a hero section`);
    logger.log(`  ${highlighter.info("/react-debug")} why does this component re-render`);
    logger.log(`  ${highlighter.info("/react-performance")} the scroll is janky`);
    logger.log(`  ${highlighter.info("/react-review")} before I commit`);
    logger.break();
    logger.log(
      `${highlighter.dim("Run")} ${highlighter.info("npx react-stack@latest list")} ${highlighter.dim("to see what's installed, or visit")} ${highlighter.info(DOCS_URL)}${highlighter.dim(".")}`,
    );
  }
  if (failedCount > 0) {
    logger.error(`${failedCount} install(s) failed. Visit ${DOCS_URL} for manual setup.`);
    process.exitCode = 1;
  }
  logger.break();
};

export const init = new Command()
  .name("init")
  .alias("setup")
  .alias("install")
  .description("initialize React Stack skills into your coding agents")
  .option("-y, --yes", "skip confirmation prompts", false)
  .option("-s, --skill <skills...>", "install specific skills by name")
  .option("-a, --agent <agents...>", "install only to the named agents")
  .option("-g, --global", "install to the per-user skills directory", false)
  .option("--copy", "copy skill files instead of symlinking", false)
  .option("-c, --cwd <cwd>", "working directory (defaults to current directory)", process.cwd())
  .action(async (options: InitOptions) => {
    printBanner(VERSION);

    try {
      const isNonInteractive = detectNonInteractive(Boolean(options.yes));
      const skillsDir = resolveBundledSkillsDir();

      const discoverSpinner = spinner("Discovering React Stack skills.").start();
      const availableSkills = await discoverSkills(skillsDir);
      if (availableSkills.length === 0) {
        discoverSpinner.fail("No React Stack skills were found.");
        logger.break();
        logger.log(`Visit ${highlighter.info(DOCS_URL)} to report this issue.`);
        logger.break();
        process.exit(1);
      }
      discoverSpinner.succeed(
        `Discovered ${availableSkills.length} React Stack skill${availableSkills.length === 1 ? "" : "s"}.`,
      );
      logger.break();

      const selection = await resolveSelection(options, availableSkills, isNonInteractive);

      if (selection.skills.length === 0) {
        logger.log("No matching skills to install.");
        logger.break();
        process.exit(1);
      }

      if (selection.agents.length === 0) {
        logger.log("No matching agents to install into.");
        logger.break();
        process.exit(1);
      }

      logger.break();
      await installSelectedSkills(skillsDir, selection, options);
    } catch (error) {
      handleError(error);
    }
  });
